/**
 * insightsMapping.js — the browser half of the family report snapshot (#583/#584 N1 slice S12; design D6).
 *
 * ONE STORED SNAPSHOT, EVERY REPRESENTATION. The server stores a snapshot (S10) and serves its facts pages
 * and its CSV from that stored row only (S11). This module turns that one snapshot into what the screen
 * draws: each metric's rows, in stored order, each with the words its chart bar and its table cell both
 * say. The bar and the cell are the SAME row object, so the chart cannot show a number the table does
 * not. Nothing here adds, averages or re-sums a value: money stays the stored decimal text, and a bar's
 * length is scaled from that same row.
 *
 * FAIL CLOSED. A snapshot that is missing, malformed or carries a metric or coverage this screen does not
 * know maps to `null`, and the screen then shows no numbers at all. An unknown value is "Not recorded",
 * never a zero.
 *
 * The family activity list is the D-60 list (`familyActivity.js` owns the only-`true`-is-on rule) with the
 * Q4 metric, approved XP. It keeps the server's name order and adds no rank.
 */
import { familyActivityRows } from './familyActivity.js';

export const METRICS = ['saved_completions', 'approved_work', 'completion_timing', 'settled_money', 'contribution_distribution'];
const COVERAGE = { full: null, partial: 'Partly recorded: this period starts before the records do', none: 'Before records began' };
const SERIES_LABELS = { approved: 'Approved on this date', no_review_credit: 'Credited without review on this date' };

/** The first report: the household's last 28 days (the server dates it), everyone, by day, every metric. */
export const DEFAULT_FILTERS = Object.freeze({ startDate: null, endDate: null, profileIds: 'all', bin: 'day', metrics: METRICS });

/** The POST body `insightSnapshotBody` validates. Each chosen date is sent; the server explains incomplete ranges. */
export function toSnapshotBody(filters) {
  const body = {};
  if (filters.startDate) body.startDate = filters.startDate;
  if (filters.endDate) body.endDate = filters.endDate;
  return { ...body, profileIds: filters.profileIds, bin: filters.bin, metrics: METRICS.filter((m) => filters.metrics.includes(m)) };
}

const isObj = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const isStr = (v) => typeof v === 'string' && v.length > 0;
const MONEY = /^-?\d+\.\d{2}$/;
// The id goes into the drill-in and CSV URLs, so only a report id's own shape is accepted.
const REPORT_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const validDate = (value) => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
  && Number.isFinite(Date.parse(value)) && new Date(value).toISOString().slice(0, 10) === value;

function rowValid(r) {
  if (!isObj(r) || !isStr(r.id) || !METRICS.includes(r.metric) || !(r.coverage in COVERAGE)) return false;
  // Timing says "N of M on time": its counts must be real numbers wherever records exist (all null only under 'none').
  if (r.metric === 'completion_timing' && r.coverage !== 'none'
    && ![r.numerator, r.denominator, r.unknown].every(Number.isFinite)) return false;
  if (r.value === null) return true;
  return r.metric === 'settled_money' ? typeof r.value === 'string' && MONEY.test(r.value) && isStr(r.currency)
    : Number.isFinite(r.value);
}

function display(r, metric) {
  if (r.coverage === 'none') return 'Not recorded';
  if (r.metric === 'completion_timing') {
    if (r.value === null) return metric.empty || 'No timing data';
    const unknown = r.unknown > 0 ? ` · ${r.unknown} with no due time` : '';
    return `${r.numerator} of ${r.denominator} on time${unknown}`;
  }
  if (r.value === null) return 'Not recorded';
  if (r.metric === 'settled_money') return `${r.currency} ${r.value}`;
  if (r.metric === 'contribution_distribution' && /xp$/.test(r.series)) return `${r.value} XP`;
  return String(r.value);
}

/** The stored snapshot, ready to draw, or `null` when any part of it cannot be trusted. */
export function toReport(snapshot) {
  if (!isObj(snapshot) || !REPORT_ID.test(String(snapshot.id)) || !isStr(snapshot.asOf) || !isStr(snapshot.expiresAt) || !isStr(snapshot.timezone)) return null;
  const { rows, metrics, bins, profiles, range } = snapshot;
  if (![rows, metrics, bins, profiles].every(Array.isArray) || !isObj(range) || !rows.every(rowValid)) return null;
  if (!validDate(range.startDate) || !validDate(range.endDate) || range.startDate > range.endDate) return null;
  if (!metrics.every((m) => isObj(m) && METRICS.includes(m.key) && isStr(m.label))) return null;
  const binById = new Map(bins.filter(isObj).map((b) => [b.id, b]));
  const people = profiles.filter(isObj).map((p) => ({ id: p.id, name: String(p.name ?? 'Member'),
    label: p.archived ? `${p.name ?? 'Member'} (archived)` : String(p.name ?? 'Member') }));
  const nameOf = (id) => people.find((p) => p.id === id)?.label ?? 'Member';
  const binLabel = (id) => {
    const b = binById.get(id);
    if (!b) return String(id);
    const span = b.startDate === b.endDate ? b.startDate : `${b.startDate} to ${b.endDate}`;
    return b.partial ? `${span} (part of a week)` : span;
  };
  return {
    id: snapshot.id, asOf: snapshot.asOf, expiresAt: snapshot.expiresAt, timezone: snapshot.timezone,
    range: { startDate: range.startDate, endDate: range.endDate }, profiles: people,
    // The stored, normalized filters in the form's shape, so a form started from this report asks for it again.
    filters: { startDate: range.startDate, endDate: range.endDate, bin: snapshot.filters?.bin === 'week' ? 'week' : 'day',
      profileIds: Array.isArray(snapshot.filters?.profiles) ? snapshot.filters.profiles : 'all',
      metrics: metrics.map((m) => m.key) },
    metrics: metrics.map((m) => {
      const seriesLabel = (key) => m.series?.find((s) => s.key === key)?.label ?? SERIES_LABELS[key] ?? m.label;
      return {
        key: m.key, label: m.label, basis: m.basis, unit: m.unit,
        rows: rows.filter((r) => r.metric === m.key).map((r) => ({
          id: r.id, series: r.series, coverage: r.coverage, coverageLabel: COVERAGE[r.coverage],
          // One chart and one table per group: a series, or for money one per stored currency.
          group: r.metric === 'settled_money' ? r.currency : r.series,
          seriesLabel: r.metric === 'settled_money' ? `Settled in ${r.currency}` : seriesLabel(r.series),
          label: r.bin === 'range' ? nameOf(r.profileId) : binLabel(r.bin),
          display: display(r, m),
          // The bar's length only. Money is parsed from its stored text to size a bar, never to add.
          amount: r.coverage === 'none' || r.value === null ? null : Number(r.value),
        })),
      };
    }),
  };
}

/** A time in the household's own zone, e.g. "Sep 7, 12:00 PM". */
function localTime(iso, timeZone) {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return 'an unrecorded time';
  return new Intl.DateTimeFormat('en-US', { timeZone, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(t);
}

const TIMING = { on_time: 'on time', late: 'late', unknown: 'no due time recorded' };

/** One stored fact, said in words. Only what the fact recorded: no quest name is inferred when none is stored. */
export function factLine(fact, report) {
  const who = report.profiles.find((p) => p.id === fact.profileId)?.label ?? 'Member';
  const at = (iso) => localTime(iso, report.timezone);
  switch (fact.type) {
    case 'completion':
      return `${who} · ${fact.choreName || 'Quest name not recorded'} · saved for ${at(fact.trackedAt)} (as recorded) · `
        + `${TIMING[fact.timing] ?? 'no due time recorded'} · review: ${fact.review}`;
    case 'approval':
      return `${who} · approved ${at(fact.decidedAt)} · ${fact.points} XP`;
    case 'no_review_credit':
      return `${who} · credited without review ${at(fact.qualifiedAt)} · ${fact.xp} XP`;
    case 'settlement':
      return `${who} · settled ${fact.currency} ${fact.amount} · ${at(fact.settledAt)} · ${fact.kind}`;
    default:
      return `${who} · a record this screen cannot describe`;
  }
}

/**
 * A day in the household's own zone, said the way the report's as-of line says it ("Sep 6, 2026"), or `null`.
 * D-I (#583 live run): the activity note printed the raw instant ("records begin 2026-09-29T16:36:51.325Z").
 */
function localDate(iso, timeZone) {
  const t = typeof iso === 'string' ? Date.parse(iso) : NaN;
  if (!Number.isFinite(t) || !isStr(timeZone)) return null;
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone, dateStyle: 'medium' }).format(t);
  } catch {
    return null;
  }
}

/** The D-60 list with its Q4 metric. Off, broken or missing a metric: no member at all. */
export function toActivity(payload) {
  const metric = isObj(payload) ? payload.metric : null;
  const rows = isObj(metric) && metric.key === 'approved_xp' ? familyActivityRows(payload) : [];
  if (!rows.length && !(isObj(metric) && payload.enabled === true)) return { status: 'off', rows: [] };
  const recordsBegin = localDate(metric.coverageFrom, metric.timezone);
  return {
    status: 'on', basis: metric.basis,
    coverageNote: metric.coverage === 'full' ? null : recordsBegin
      ? `Partly recorded: records begin ${recordsBegin}. Earlier totals may be incomplete.`
      : 'Partly recorded: when records begin is not known. Totals may be incomplete.',
    heading: `${metric.label}, ${metric.startDate} to ${metric.endDate}`,
    rows: rows.map((r) => ({ id: r.user_id, name: r.first_name || 'A family member',
      display: !Number.isFinite(r.approved_xp) || (metric.coverage !== 'full' && r.approved_xp === 0)
        ? 'Not recorded' : `${r.approved_xp} approved XP${metric.coverage === 'full' ? '' : ' · Partly recorded'}` })),
  };
}

/** What a refused or failed request means, in the family's words. */
export function reportMessage(error) {
  const code = error?.body?.code;
  switch (error?.status) {
    case 401: return 'Your sign-in has expired. Please sign in again.';
    case 403: return 'Only a parent of this family can see family reports.';
    case 404: return 'That report is no longer available. Build it again to see the numbers.';
    case 410: return code === 'SNAPSHOT_WITHDRAWN'
      ? 'This report was withdrawn and is no longer available. Build it again.'
      : 'This report has expired. Build it again to see current numbers.';
    case 422: return 'That range has more than 10,000 records. Choose a shorter range.';
    case 400: {
      const messages = Array.isArray(error.body?.details)
        ? error.body.details.map((issue) => issue?.message).filter(isStr) : [];
      if (messages.length) return messages.join('. ');
      if (isStr(error.body?.error)) return error.body.error;
      break;
    }
    case 409: case 429:
      if (isStr(error.body?.error)) return error.body.error;
      break;
    default:
  }
  return 'We could not build your report just now. Nothing was changed. Give it another moment.';
}
